import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface ExtractedField {
  boxNumber?: string;
  fieldName: string;
  value: string | number;
  confidence?: number;
}

export interface DocumentAnalysisResult {
  documentId: number;
  documentType: string;
  confidence: number;
  extractedFields: ExtractedField[];
  issuerPayerInfo?: {
    name: string;
    ein?: string;
    address?: string;
  };
  observations: string[];
  warnings: Array<{
    severity: 'warning' | 'error';
    message: string;
  }>;
  rawData?: unknown;
}

export interface VerificationField {
  docIndex: number;
  sourceDocumentType: string;
  fieldName: string;
  displayLabel: string;
  extractedValue: number;
  confidence?: number;
  recipientName?: string;
  sourceName?: string;
}

export interface FullReturnAnalysis {
  taxYear: number;
  analysisId?: number;
  verificationStatus?: 'unverified' | 'verified' | 'corrected';
  extractedFields?: VerificationField[];
  incomeSummary: {
    lineItems: Array<{
      label: string;
      amount: number;
      source: string; // documentId or source
    }>;
    totalIncome: number;
  };
  deductionAnalysis: {
    standardDeduction: number;
    itemizedDeductions: number;
    deductionItems: Array<{
      label: string;
      amount: number;
    }>;
    recommendation: 'standard' | 'itemized';
    potentialSavings: number;
  };
  creditsIdentified: Array<{
    name: string;
    estimatedValue: number;
    reason: string;
  }>;
  taxCalculation: {
    grossIncome: number;
    adjustedGrossIncome: number;
    taxableIncome: number;
    estimatedTax: number;
    credits: number;
    taxWithheld: number;
    refundOrDue: number;
  };
  riskFlags: Array<{
    severity: 'info' | 'warning' | 'error';
    message: string;
  }>;
  narrativeSummary?: string;
  filingComparison?: {
    mfjTax: number;
    mfjRefund: number;
    mfsTax: number;
    savings: number;
    recommendation: string;
    recommendationText: string;
    mfsBreakdown: {
      person1: { name: string; tax: number; refund: number };
      person2: { name: string; tax: number; refund: number };
    };
    mfsDisqualifiedCredits: string[];
  };
  documentPersonAssignments?: Array<{
    docIndex: number;
    person: string;
    docType: string;
  }>;
  disclaimerText?: string;
  yearOverYearComparison?: {
    incomeChangePct: number | null;
    notableChanges?: string[];
    estimatedPaymentAdequacy?: string | null;
  };
  savingsOpportunities?: Array<{
    priority: 'high' | 'medium' | 'low';
    category: string;
    title: string;
    description: string;
    estimatedSavings: number;
  }>;
  taxPlanningConsiderations?: Array<{
    category?: string;
    consideration: string;
    potential_impact?: string;
    timeframe?: string;
  }>;
}

export interface TaxPlanningConsideration {
  category: string;
  consideration: string;
  potentialImpact: string;
  timeframe: 'current_year' | 'next_year' | 'ongoing';
}

export interface TaxSuggestions {
  taxYear: number;
  filingRecommendation: {
    status: string;
    estimatedSavings: number;
  };
  deductionComparison: {
    standard: number;
    itemized: number;
    recommended: 'standard' | 'itemized';
    savings: number;
  };
  creditsList: Array<{
    name: string;
    value: number;
    description: string;
  }>;
  actionItems: Array<{
    title: string;
    description: string;
    priority: 'high' | 'medium' | 'low';
    completed?: boolean;
  }>;
  riskItems: Array<{
    type: string;
    message: string;
    severity: 'info' | 'warning' | 'error';
  }>;
  taxPlanningConsiderations?: TaxPlanningConsideration[];
  disclaimerText?: string;
  yearOverYearComparison?: {
    incomeChangePct: number | null;
    notableChanges: string[];
    estimatedPaymentAdequacy: string | null;
  };
}

export interface AnalysisState {
  documentAnalyses: Record<number, DocumentAnalysisResult>;
  fullReturnAnalysis: FullReturnAnalysis | null;
  taxSuggestions: TaxSuggestions | null;
  analysisLoading: boolean;
  analysisError: string | null;
  analyzingDocumentId: number | null;
  verificationLoading: boolean;
  selectedTaxYear: number;
}

const initialState: AnalysisState = {
  documentAnalyses: {},
  fullReturnAnalysis: null,
  taxSuggestions: null,
  analysisLoading: false,
  analysisError: null,
  analyzingDocumentId: null,
  verificationLoading: false,
  selectedTaxYear: 2025,
};

const analysisSlice = createSlice({
  name: 'analysis',
  initialState,
  reducers: {
    setDocumentAnalysis: (
      state,
      action: PayloadAction<{ documentId: number; analysis: DocumentAnalysisResult }>
    ) => {
      state.documentAnalyses[action.payload.documentId] = action.payload.analysis;
      state.analysisError = null;
    },

    setFullReturnAnalysis: (state, action: PayloadAction<FullReturnAnalysis>) => {
      state.fullReturnAnalysis = action.payload;
      state.analysisError = null;
    },

    setTaxSuggestions: (state, action: PayloadAction<TaxSuggestions>) => {
      state.taxSuggestions = action.payload;
    },

    setAnalysisLoading: (state, action: PayloadAction<boolean>) => {
      state.analysisLoading = action.payload;
    },

    setAnalyzingDocumentId: (state, action: PayloadAction<number | null>) => {
      state.analyzingDocumentId = action.payload;
    },

    setAnalysisError: (state, action: PayloadAction<string | null>) => {
      state.analysisError = action.payload;
    },

    clearDocumentAnalysis: (state, action: PayloadAction<number>) => {
      delete state.documentAnalyses[action.payload];
    },

    clearAllAnalyses: (state) => {
      state.documentAnalyses = {};
      state.fullReturnAnalysis = null;
      state.taxSuggestions = null;
      state.analysisError = null;
      state.verificationLoading = false;
    },

    setVerificationLoading: (state, action: PayloadAction<boolean>) => {
      state.verificationLoading = action.payload;
    },

    setSelectedTaxYear: (state, action: PayloadAction<number>) => {
      state.selectedTaxYear = action.payload;
      // Clear analysis when year changes — force re-analysis
      state.fullReturnAnalysis = null;
      state.taxSuggestions = null;
      state.analysisError = null;
    },
  },
});

export const {
  setDocumentAnalysis,
  setFullReturnAnalysis,
  setTaxSuggestions,
  setAnalysisLoading,
  setAnalyzingDocumentId,
  setAnalysisError,
  clearDocumentAnalysis,
  clearAllAnalyses,
  setVerificationLoading,
  setSelectedTaxYear,
} = analysisSlice.actions;

export default analysisSlice.reducer;
